import React, { useState } from "react";
import _ from "lodash";

import { ConcreteHammerData, ConcreteHammerReport, ConcreteHammerReportItem, PDFImageProps } from "../../../../utils/models";
import { getValueRange, matchPhrases } from "../../../../utils/utilities";
import { getConcreteInspectionItemColumns } from "../../../../utils/data";

import AccordionItem from "../../custom-visual-inspection/accordion-item";
import AutTable from "../../../blocks/aut-grid-table";
import ConcreteHammerReportForm from "../../../forms/custom-pcnl/concreteHammerReportForm";
import Button from "../../../themed/button";
import Conditional from "../../../common/conditional";
import ConcreteHammerRangeFormEdit from "../../../forms/custom-pcnl/concreteHammerRangeFormEdit";
import ConcreteHammerRangeForm from "../../../forms/custom-pcnl/concreteHammerRangeForm";
import ConcreteHammerInspectionItemEdit from "../../../forms/custom-pcnl/concreteHammerInspectionItemFormEdit";
import ConcreteHammerInspectionItem from "../../../forms/custom-pcnl/concreteHammerInspectionItemForm";

import toastService from "../../../../services/toastService";
import MUTIsoPageForm from "../../../forms/mut/mutIsoPageForm";

interface Props{
   item: ConcreteHammerData;
   onDataChange: (item: ConcreteHammerData) => void;
}

const ConcreteHammerTemplate: React.FC<Props> = ({ item, onDataChange }) => {
    const [isEditVisible, setEditVisible] = useState(false);
    const [reportItemToEdit, setReportItemToEdit] = useState<ConcreteHammerReportItem | null>(null);

    const handleSaveConcreteData = (report: ConcreteHammerReport) => {
        const pcnlConcreteHammerData: ConcreteHammerData = {
            reading: item.reading,
            overview: report,
            locations: item.locations,
        };

        onDataChange(pcnlConcreteHammerData);
    };

    const handleEditConcreteDataRange = (reading: Omit<ConcreteHammerData['reading'], 'items'>) => {
        const pcnlConcreteHammerData: ConcreteHammerData = {
            reading: { ...item.reading, ...reading },
            overview: item.overview,
            locations: item.locations,
        };

        onDataChange(pcnlConcreteHammerData);
        setEditVisible(false);
    };

    const handleAddReportItem = (inspectionItem: ConcreteHammerReportItem) => {
        const matchingLocationFound = item.reading.items.find((item) => matchPhrases(item.testLocation, inspectionItem.testLocation));
        if (matchingLocationFound) {
            return toastService.error(`${inspectionItem.testLocation} already exists!`);
        }

        const concreteData: ConcreteHammerData = {
            overview: item.overview,
            locations: item.locations,
            reading: {
                readingStartRange: item.reading.readingStartRange,
                readingEndRange: item.reading.readingEndRange,
                items: [...item.reading.items, inspectionItem],
            },
        };
       
        onDataChange(concreteData);
    };

    const handleDeleteReportItem = (reportItemToDelete: ConcreteHammerReportItem) => {
        const concreteData: ConcreteHammerData = {
            overview: item.overview,
            locations: item.locations,
            reading: {
                readingStartRange: item.reading.readingStartRange,
                readingEndRange: item.reading.readingEndRange,
                items: item.reading.items.filter((reportItem) => reportItem.testLocation !== reportItemToDelete.testLocation)
            },
        };

        onDataChange(concreteData);
        setReportItemToEdit(null);
    };
  
    const handleUpdateReportItem = (updatedReportItem: ConcreteHammerReportItem) => {
        const concreteData: ConcreteHammerData = {
            overview: item.overview,
            locations: item.locations,
            reading: {
                readingStartRange: item.reading.readingStartRange,
                readingEndRange: item.reading.readingEndRange,
                items: item.reading.items.map((reportItem) => reportItem.testLocation === reportItemToEdit!.testLocation ? updatedReportItem : reportItem)
            },
        };

        onDataChange(concreteData);
        setReportItemToEdit(null);
    };

    const handleLocationsUpdate = (locations: PDFImageProps[]) => {
        const pcnlConcreteHammerData: ConcreteHammerData = {
            reading: item.reading,
            overview: item.overview,
            locations,
        };

        onDataChange(pcnlConcreteHammerData);
        setEditVisible(false);
    };

    return ( 
        <>
            <AccordionItem isActive title="Concrete Hammer Test Locations">
				<MUTIsoPageForm initialImages={item.locations} onProceed={handleLocationsUpdate} />
			</AccordionItem>

            <AccordionItem isActive title="Concrete Hammer Test Report Overview Data">
                <ConcreteHammerReportForm
                    initialConcreteData={item.overview}
                    onProceed={handleSaveConcreteData}
                />
            </AccordionItem>

            <AccordionItem isActive title="Concrete Hammer Test Report Readings">
                <Conditional isVisible={isEditVisible}>
                    <ConcreteHammerRangeFormEdit
                        onCancelEdit={() => setEditVisible(false)}
                        initialRangeData={_.pick(item.reading, ['readingStartRange', 'readingEndRange'])}
                        onEdit={handleEditConcreteDataRange}
                    />
                </Conditional>

                <Conditional isVisible={!isEditVisible}>
                    <Conditional isVisible={item.reading.readingStartRange === 0 || item.reading.readingEndRange === 0}>
                        <ConcreteHammerRangeForm onSave={handleEditConcreteDataRange} />
                    </Conditional>

                    <Conditional isVisible={item.reading.readingStartRange !== 0 && item.reading.readingEndRange !== 0}>
                        <div className="mb-4 d-flex justify-content-end">
                            <Button
                                label="Edit Reading Data"
                                icon="pencil"
                                onClick={() => setEditVisible(true)}
                                style={{ backgroundColor: 'red' }}
                            />
                        </div>

                        <Conditional isVisible={reportItemToEdit !== null}>
                            <ConcreteHammerInspectionItemEdit
                                range={getValueRange(item.reading.readingStartRange, item.reading.readingEndRange)}
                                onCancelEdit={() => setReportItemToEdit(null)}
                                initialInspectionItem={reportItemToEdit}
                                onEdit={handleUpdateReportItem}
                            />
                        </Conditional>

                        <Conditional isVisible={reportItemToEdit === null}>
                            <ConcreteHammerInspectionItem
                                range={getValueRange(item.reading.readingStartRange, item.reading.readingEndRange)}
                                onAdd={handleAddReportItem}
                            />

                            <Conditional isVisible={item.reading.items.length > 0}>
                                <div className="divider my-4"></div>

                                <AutTable
                                    tableColumns={getConcreteInspectionItemColumns(item.reading.readingStartRange, item.reading.readingEndRange)}
                                    data={item.reading.items}
                                    onEditItem={(reportItem) => setReportItemToEdit(reportItem) }
                                    onDeleteItem={handleDeleteReportItem}
                                />
                            </Conditional>
                        </Conditional>
                    </Conditional>
                </Conditional>
            </AccordionItem>
        </>
    );
};
 
export default ConcreteHammerTemplate;